// Leader action center — the viewer-scoped aggregate behind the /feed
// hero module and its rail cards. One React Query entry; the module and
// the rail both read it, so simultaneous callers share one request.

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@/lib/api-client';
import type { components } from '@leanin/api-client';
import { QUERY_KEY_REGISTRY } from './normalize';

export type LeaderActions = components['schemas']['LeaderActions'];
export type LeaderBoard = components['schemas']['LeaderBoard'];
export type LeaderTask = components['schemas']['LeaderTask'];
export type LeaderRole = LeaderActions['role'];

async function fetchLeaderActions(): Promise<LeaderActions> {
  const client = getApiClient();
  const res = await client.GET('/api/v1/me/leader-actions');
  if (res.data === undefined) {
    throw new Error('Failed to load leader actions');
  }
  return res.data;
}

export const leaderActionsKeys = {
  me: () =>
    queryOptions({
      queryKey: ['leader-actions', 'me'] as const,
      queryFn: fetchLeaderActions,
      // The feed's scope-settling gate serializes every cookie-less
      // visitor's first feed fetch behind this query. The global policy
      // (3 retries, exponential backoff) would let a 5xx here hold the
      // whole feed in skeletons for ~7s — fail fast instead; the gate
      // falls back to the 'all' tab on error.
      retry: 1,
    }),
};

QUERY_KEY_REGISTRY.push({
  name: 'leaderActionsKeys.me',
  sampleKey: leaderActionsKeys.me().queryKey,
});

export function useLeaderActions() {
  return useQuery(leaderActionsKeys.me());
}

// Dismiss one task row for 7 days (server-side, follows the account).
// Optimistically drops the row from every board; the refetch reconciles.
export function useDismissLeaderTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskKey: string) => {
      const client = getApiClient();
      const { response } = await client.POST('/api/v1/me/leader-actions/dismissals', {
        body: { taskKey },
      });
      if (!response.ok) throw new Error(`leader-task dismissal failed (${response.status})`);
    },
    onMutate: async (taskKey) => {
      await qc.cancelQueries({ queryKey: leaderActionsKeys.me().queryKey });
      const previous = qc.getQueryData<LeaderActions>(leaderActionsKeys.me().queryKey);
      if (previous !== undefined) {
        qc.setQueryData<LeaderActions>(leaderActionsKeys.me().queryKey, {
          ...previous,
          boards: previous.boards.map((b) => ({
            ...b,
            tasks: b.tasks.filter((t) => t.key !== taskKey),
          })),
        });
      }
      return { previous };
    },
    onError: (_err, _key, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(leaderActionsKeys.me().queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: leaderActionsKeys.me().queryKey });
    },
  });
}
